'use client';
import React, {useMemo} from 'react';
import styles, {LaunchCarousel} from './styles';
import {ImgProps} from '../../../types/ImgProps';
import Image from '@new-components/atoms/Image';
import ReactHtmlParser from '@libs/reactHtmlParser';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiContainer from '@new-components/atoms/MuiContainer';
import MuiTypography from '@new-components/atoms/MuiTypography';
import MuiLink from '@new-components/atoms/MuiLink';
import {Settings} from 'react-slick';

interface NewLaunchSlideProps {
  bgImg: ImgProps;
  logoImg: ImgProps;
  title: string;
  subTitle: string;
  exploreButtonLabel: string;
  exploreLink: string;
  backdropBg?: string;
  backdropOpacity?: string;
}

const NewLaunchSlide: React.FC<NewLaunchSlideProps> = React.memo((props) => {
  return (
    <MuiBox sx={styles.slideWrapper} data-testid='new-launch-slide-wrapper'>
      <MuiBox sx={styles.bgWrapper}>
        <MuiBox
          sx={{
            ...styles.imageOverlay,
            ...(props.backdropBg ? {background: props.backdropBg} : {}),
            ...(props.backdropOpacity ? {opacity: props.backdropOpacity} : {}),
          }}
          data-testid='new-launch-slide-image-overlay'
        />
        <Image
          src={props.bgImg.src}
          data-src={props.bgImg.src}
          alt={props.bgImg.alt}
          priority
          quality={100}
          style={{objectFit:"cover"}}
          fill
        />
      </MuiBox>
      <MuiContainer sx={styles.contentWrapper}>
        <MuiBox
          sx={styles.detailsWrapper}
          data-testid='new-launch-slide-details'>
          <MuiBox sx={styles.logoWrapper}>
            <MuiBox sx={styles.logoDesktop}>
              <Image
                src={props.logoImg.src}
                data-src={props.logoImg.src}
                width={200}
                height={100}
                style={{
                  objectFit: 'contain',
                }}
                alt={props.logoImg.alt}
              />
            </MuiBox>
            <MuiBox sx={styles.logoMobile}>
              <Image
                src={props.logoImg.src}
                data-src={props.logoImg.src}
                width={150}
                height={75}
                style={{
                  objectFit: 'contain',
                }}
                alt={props.logoImg.alt}
              />
            </MuiBox>
          </MuiBox>
          <MuiTypography data-testid='new-launch-slide-title' sx={styles.title}>
            {ReactHtmlParser(props.title)}
          </MuiTypography>
          <MuiTypography
            data-testid='new-launch-slide-subtitle'
            sx={styles.subTitle}>
            {ReactHtmlParser(props.subTitle)}
          </MuiTypography>
          <MuiBox sx={styles.linkWrapper}>
            <MuiLink
              href={props.exploreLink}
              data-testid='new-launch-slide-explore-link'
              sx={styles.exploreLink}>
              {props.exploreButtonLabel}
            </MuiLink>
          </MuiBox>
        </MuiBox>
        <MuiBox
          sx={styles.filterWrapper}
          data-testid='new-launch-slide-filter-wrapper'></MuiBox>
      </MuiContainer>
    </MuiBox>
  );
});

interface NewLaunchesCarouselProps {
  featuredLaunch: Array<NewLaunchSlideProps>;
}

const NewLaunchesCarousel: React.FC<NewLaunchesCarouselProps> = (props) => {
  const carouselSetting: Settings = useMemo(() => {
    return {
      'data-testid': 'new-launches-carousel',
      'slidesToShow': 1,
      'dots': true,
      'swipeToSlide': true,
      'fade': true,
      'infinite': true,
      'autoplay': true,
      'autoplaySpeed': 5000,
      'pauseOnHover': false,
      'speed': 1000,
      'cssEase': 'ease-in-out',
    };
  }, []);
  return (
    <LaunchCarousel {...carouselSetting}>
      {props.featuredLaunch.map((launch, index) => (
        <NewLaunchSlide key={`launch-${index}-${launch.title}`} {...launch} />
      ))}
    </LaunchCarousel>
  );
};

interface HomeBannerProps {
  label: string;
  children: React.ReactNode;
  newLaunches: Array<NewLaunchSlideProps>;
}
const HomeBanner: React.FC<HomeBannerProps> = (props) => {
  return (
    <MuiBox sx={styles.container} data-testid='home-banner'>
      <NewLaunchesCarousel featuredLaunch={props.newLaunches} />
      <MuiContainer sx={styles.filterContainer}>
        <MuiBox data-testid='home-banner-filter-label' sx={styles.filterLabel}>
          {ReactHtmlParser(props.label)}
        </MuiBox>
        {props.children}
      </MuiContainer>
    </MuiBox>
  );
};

export default HomeBanner;
